import { get, post } from "../utils/apiClient";
import { jsonLoader } from "../utils/jsonLoader";
import { API_QUERY_KEYS, getCachedQuery } from "../utils/apiQueryCache";

const getLoginUserErrorMessage = (status) => {
  if (status == null || status === 0) {
    return "APIに接続できませんでした。ネットワークまたはCORS設定を確認してください。";
  }
  if (status === 400 || status === 405) {
    return "ログインユーザー情報取得APIの呼び出し先またはメソッドが正しくありません。管理者に問い合わせてください。";
  }
  if (status === 401) {
    return "認証情報を確認できませんでした。再度ログインしてください。";
  }
  if (status === 403) {
    return "管理ユーザーが無効です。管理者に問い合わせてください。";
  }
  if (status === 404) {
    return "Cognitoユーザーに紐づく管理ユーザー情報が見つかりません。管理者に問い合わせてください。";
  }
  if (status >= 500) {
    return "サーバーエラーが発生しました。時間を置いて再試行してください。";
  }
  return "ログインユーザー情報の取得に失敗しました。時間を置いて再試行してください。";
};

const createLoginUserError = (error) => {
  const status = error.status ?? error.response?.status;
  const loginUserError = new Error(getLoginUserErrorMessage(status));
  loginUserError.status = status;
  loginUserError.response = error.response;
  loginUserError.code = error.code;
  loginUserError.endpoint = error.endpoint;
  return loginUserError;
};

const fetchLoginUser = async () => {
  try {
    const response = await getCachedQuery(
      API_QUERY_KEYS.loginUser,
      () =>
        get(
          process.env.REACT_APP_API_ENDPOINT + "login",
          {},
          true
        )
    );
    return response;
  } catch (error) {
    throw createLoginUserError(error);
  }
};

const getUserList = async () => {
  try {
    const response = await getCachedQuery(
      API_QUERY_KEYS.userList,
      () =>
        get(
          process.env.REACT_APP_API_ENDPOINT + "user_list",
          {},
          true
        )
    );
    return response;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const postAnswer = async (answerData) => {
  try {
    const response = await post(
      process.env.REACT_APP_API_ENDPOINT + "answer",
      answerData,
      false
    );
    return response;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const getQuestionPart = async () => {
  try {
    const endpoint = process.env.REACT_APP_API_ENDPOINT + "question_part";
    const response = await get(endpoint, {}, false);
    return response;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const getUserDetail = async (userid) => {
  try {
    const response = await getCachedQuery(
      API_QUERY_KEYS.userDetail(userid),
      () =>
        get(
          process.env.REACT_APP_API_ENDPOINT + "user",
          {
            user_id: userid,
          },
          true
        )
    );
    return response;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const postUserUpdate = async (userData) => {
  try {
    const response = await post(
      process.env.REACT_APP_API_ENDPOINT + "user",
      userData,
      true
    );
    return response;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const getQuestionList = async () => {
  try {
    const response = await getCachedQuery(
      API_QUERY_KEYS.questionList,
      () =>
        get(
          process.env.REACT_APP_API_ENDPOINT + "question_list",
          {},
          true
        )
    );
    return response;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const getQuestionDetail = async (questionId) => {
  try {
    console.log("question_id:" + questionId);
    const endpoint = process.env.REACT_APP_API_ENDPOINT + "question";
    const params = {
      question_id: String(questionId),
    };
    const response = await getCachedQuery(
      API_QUERY_KEYS.questionDetail(questionId),
      () => get(endpoint, params, true)
    );
    return response;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const postEditQuestion = async (questionData) => {
  try {
    const response = await post(
      process.env.REACT_APP_API_ENDPOINT + "question",
      questionData,
      true
    );
    return response;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const postPassSetting = async (passData) => {
  try {
    const response = await post(
      process.env.REACT_APP_API_ENDPOINT + "pass",
      passData,
      true
    );
    return response;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const postTopInfo = async (topInfoData) => {
  try {
    const response = await post(
      process.env.REACT_APP_API_ENDPOINT + "top_info",
      topInfoData,
      false
    );
    return response;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const getAdminUserList = async () =>
  getCachedQuery(
    API_QUERY_KEYS.adminUserList,
    () => get(process.env.REACT_APP_API_ENDPOINT + "admin_user_list", {}, true)
  );

const postAdminUserUpdate = async (adminUserData) =>
  post(process.env.REACT_APP_API_ENDPOINT + "admin_user", adminUserData, true);

const postAdminUserCreate = async (adminUserData, idempotencyKey) =>
  post(
    process.env.REACT_APP_API_ENDPOINT + "admin_user_create",
    adminUserData,
    true,
    {
      headers: { "X-Idempotency-Key": idempotencyKey },
      includeResponse: true,
    }
  );

const getCompanyList = async () =>
  getCachedQuery(
    API_QUERY_KEYS.companyList,
    () => get(process.env.REACT_APP_API_ENDPOINT + "company_list", {}, true)
  );

const postCompanyCreate = async (companyData) =>
  post(
    process.env.REACT_APP_API_ENDPOINT + "company_create",
    companyData,
    true,
    { includeResponse: true }
  );

const postCompanyUpdate = async (companyData) =>
  post(process.env.REACT_APP_API_ENDPOINT + "company", companyData, true);

const postPasswordResetRequest = async (email) =>
  post(
    process.env.REACT_APP_API_ENDPOINT + "password_reset/request",
    { email },
    false
  );

const postPasswordResetConfirm = async (email, confirmationCode, newPassword) =>
  post(
    process.env.REACT_APP_API_ENDPOINT + "password_reset/confirm",
    {
      email,
      confirmation_code: confirmationCode,
      new_password: newPassword,
    },
    false
  );

export {
  fetchLoginUser,
  getUserList,
  postAnswer,
  getQuestionPart,
  getUserDetail,
  postUserUpdate,
  getQuestionList,
  getQuestionDetail,
  postEditQuestion,
  postPassSetting,
  postTopInfo,
  getAdminUserList,
  postAdminUserUpdate,
  postAdminUserCreate,
  getCompanyList,
  postCompanyCreate,
  postCompanyUpdate,
  postPasswordResetRequest,
  postPasswordResetConfirm,
};
